import type { PlayerRoleType } from '@repo/sdk'
import {
  IMPLEMENTED_GAME_ROLES,
  LANGUAGE_ENUM_VALUES,
  RoleFaction,
} from '@repo/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useI18n } from '@/lib/i18n'
import { type GameplayGuidance, useTRPC } from '@/lib/trpc'

type Language = (typeof LANGUAGE_ENUM_VALUES)[number]
type Category = 'faction' | 'sheriff' | 'roles'
type ActualRole = Exclude<PlayerRoleType, 'lucky_one'>
const ACTUAL_ROLES = IMPLEMENTED_GAME_ROLES.filter(
  (role): role is ActualRole => role !== 'lucky_one',
)
type Draft = { saved: GameplayGuidance, value: GameplayGuidance }
type PreviewInput = {
  language: Language
  roles: PlayerRoleType[]
  hasSheriff: boolean
  role: ActualRole
  guidance: GameplayGuidance
}
const LANGUAGE_LABELS: Record<Language, string> = {
  zh: '简体中文',
  tw: '繁體中文',
  en: 'English',
  fr: 'Français',
  es: 'Español',
  ja: '日本語',
  ko: '한국어',
  ar: 'العربية',
}
const selectClass
  = 'h-10 max-w-full rounded-md border border-input bg-background px-3 text-sm text-foreground'
function same(left: GameplayGuidance, right: GameplayGuidance) {
  return JSON.stringify(left) === JSON.stringify(right)
}

function GuidancePreview({
  input,
  stale,
  dirty,
}: {
  input: PreviewInput
  stale: boolean
  dirty: boolean
}) {
  const { t } = useI18n()
  const trpc = useTRPC()
  const [copied, setCopied] = useState('')
  const [copyError, setCopyError] = useState(false)
  const { data, error, isFetching, refetch } = useQuery(
    trpc.queryRouter.previewGameplayGuidance.queryOptions(input, {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    }),
  )
  const text = data?.guidance ?? ''
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(text)
      setCopyError(false)
    }
    catch {
      setCopyError(true)
    }
  }
  return (
    <div className="space-y-3" aria-busy={isFetching}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground" role="status">
          {t(
            stale
              ? 'admin.guidance.previewStale'
              : dirty
                ? 'admin.guidance.previewUnsaved'
                : 'admin.guidance.previewSaved',
          )}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={copy}
          disabled={!text || stale || isFetching || Boolean(error)}
          data-testid="guidance-copy"
        >
          {copied === text && text ? (
            <Check className="mr-2 size-4" />
          ) : (
            <Copy className="mr-2 size-4" />
          )}
          {t(
            copied === text && text
              ? 'admin.promptInspector.copied'
              : 'admin.promptInspector.copy',
          )}
        </Button>
      </div>
      {isFetching ? (
        <p
          className="flex items-center gap-2 text-sm text-muted-foreground"
          role="status"
        >
          <Loader2 className="size-4 animate-spin" />
          {t('admin.guidance.previewLoading')}
        </p>
      ) : error ? (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>
            {t('admin.guidance.previewFailed')} {error.message}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetch()
            }}
          >
            {t('admin.promptInspector.retry')}
          </Button>
        </div>
      ) : (
        <pre
          data-testid="guidance-preview"
          lang={input.language === 'tw' ? 'zh-Hant' : input.language}
          dir={input.language === 'ar' ? 'rtl' : 'ltr'}
          tabIndex={0}
          className={`max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted/30 p-4 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${input.language === 'ar' ? 'font-sans' : 'font-mono'}`}
        >
          {text}
        </pre>
      )}
      {copyError && (
        <p className="text-sm text-destructive" role="alert">
          {t('admin.promptInspector.copyFailed')}
        </p>
      )}
    </div>
  )
}

export function GameplayGuidanceEditor({
  language,
  onLanguageChange,
  roles,
  hasSheriff,
  roleName,
}: {
  language: Language
  onLanguageChange: (language: Language) => void
  roles: PlayerRoleType[]
  hasSheriff: boolean
  roleName: (role: string) => string
}) {
  const { t } = useI18n()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [drafts, setDrafts] = useState<Partial<Record<Language, Draft>>>({})
  const [category, setCategory] = useState<Category>('faction')
  const [faction, setFaction] = useState<'good' | 'evil'>('good')
  const [editingRole, setEditingRole] = useState<ActualRole>('seer')
  const [previewRole, setPreviewRole] = useState<ActualRole>('seer')
  const [preview, setPreview] = useState<PreviewInput | null>(null)
  const [savedLanguage, setSavedLanguage] = useState<Language | null>(null)
  const queryOptions = trpc.queryRouter.getGameplayGuidance.queryOptions(
    { language },
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  )
  const { data, error, isFetching, refetch } = useQuery(queryOptions)
  const saveMutation = useMutation(
    trpc.queryRouter.saveGameplayGuidance.mutationOptions(),
  )
  const currentDraft = drafts[language]
  const guidance = currentDraft?.value ?? data?.guidance
  const dirty = currentDraft
    ? !same(currentDraft.saved, currentDraft.value)
    : false
  const anyDirty = Object.values(drafts).some(
    draft => draft && !same(draft.saved, draft.value),
  )
  const entry = category === 'roles' ? editingRole : faction
  const body = guidance
    ? category === 'roles'
      ? guidance.roles[editingRole]
      : guidance[category][faction]
    : ''
  const previewRoles = [...new Set(roles)].filter(
    (role): role is ActualRole => role !== 'lucky_one',
  )
  const selectedPreviewRole = previewRoles.includes(previewRole)
    ? previewRole
    : previewRoles[0]
  const previewInput
    = guidance && selectedPreviewRole
      ? { language, roles, hasSheriff, role: selectedPreviewRole, guidance }
      : null
  const previewStale
    = !previewInput || JSON.stringify(previewInput) !== JSON.stringify(preview)
  const valid
    = guidance
      && [
        ...Object.values(guidance.faction),
        ...Object.values(guidance.sheriff),
        ...Object.values(guidance.roles),
      ].every(text => text.trim().length > 0 && text.trim().length <= 30_000)

  useEffect(() => {
    if (!anyDirty)
      return
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    const beforeNavigate = (event: Event) => {
      if (event instanceof CustomEvent && event.detail === 'prompt-inspector')
        return
      // eslint-disable-next-line no-alert -- The approved editor requires native leave/stay confirmation.
      if (!window.confirm(t('admin.guidance.leaveWarning')))
        event.preventDefault()
    }
    const unsavedWork = (event: Event) => event.preventDefault()
    window.addEventListener('beforeunload', beforeUnload)
    window.addEventListener('admin-before-navigate', beforeNavigate)
    window.addEventListener('admin-has-unsaved-work', unsavedWork)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      window.removeEventListener('admin-before-navigate', beforeNavigate)
      window.removeEventListener('admin-has-unsaved-work', unsavedWork)
    }
  }, [anyDirty, t])

  const changeBody = (value: string) => {
    if (!guidance)
      return
    setDrafts(previous => ({
      ...previous,
      [language]: {
        saved: previous[language]?.saved ?? guidance,
        value: {
          ...guidance,
          [category]: { ...guidance[category], [entry]: value },
        },
      },
    }))
    setSavedLanguage(null)
    saveMutation.reset()
  }
  const save = async () => {
    if (!guidance || !valid || saveMutation.isPending)
      return
    try {
      const result = await saveMutation.mutateAsync({ language, guidance })
      queryClient.setQueryData(queryOptions.queryKey, result)
      setDrafts((previous) => {
        const next = { ...previous }
        delete next[language]
        return next
      })
      setSavedLanguage(language)
    }
    catch {
      // The mutation error is shown below; retain every draft for retry.
    }
  }

  return (
    <Card className="min-w-0" data-testid="gameplay-guidance-editor">
      <CardHeader className="gap-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <CardTitle className="text-lg">{t('admin.guidance.title')}</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            <Label htmlFor="guidance-language">
              {t('admin.guidance.language')}
            </Label>
            <select
              id="guidance-language"
              className={selectClass}
              value={language}
              disabled={saveMutation.isPending}
              onChange={event =>
                onLanguageChange(event.target.value as Language)}
            >
              {LANGUAGE_ENUM_VALUES.map(code => (
                <option key={code} value={code}>
                  {LANGUAGE_LABELS[code]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('admin.guidance.description')}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {!guidance ? (
          error ? (
            <div className="space-y-3" role="alert">
              <p className="text-sm text-destructive">
                {t('admin.guidance.loadFailed')} {error.message}
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  refetch()
                }}
              >
                {t('admin.promptInspector.retry')}
              </Button>
            </div>
          ) : (
            <p
              role="status"
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <Loader2 className="size-4 animate-spin" />
              {t('admin.guidance.loading')}
            </p>
          )
        ) : (
          <>
            <Tabs
              value={category}
              onValueChange={value => setCategory(value as Category)}
            >
              <TabsList aria-label={t('admin.guidance.category')}>
                {(['faction', 'sheriff', 'roles'] as const).map(value => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    disabled={saveMutation.isPending}
                  >
                    {t(`admin.guidance.${value}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex flex-wrap items-center gap-3">
              <Label htmlFor="guidance-entry">
                {t(
                  category === 'roles'
                    ? 'admin.guidance.role'
                    : 'admin.guidance.faction',
                )}
              </Label>
              <select
                id="guidance-entry"
                className={selectClass}
                value={entry}
                disabled={saveMutation.isPending}
                onChange={event =>
                  category === 'roles'
                    ? setEditingRole(event.target.value as ActualRole)
                    : setFaction(event.target.value as 'good' | 'evil')}
              >
                {category === 'roles'
                  ? ACTUAL_ROLES.map(role => (
                      <option key={role} value={role}>
                        [{roleName(role)}]
                      </option>
                    ))
                  : (['good', 'evil'] as const).map(value => (
                      <option key={value} value={value}>
                        {t(`admin.guidance.${value}`)}
                      </option>
                    ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="guidance-body">{t('admin.guidance.body')}</Label>
              <Textarea
                id="guidance-body"
                value={body}
                disabled={saveMutation.isPending}
                onChange={event => changeBody(event.target.value)}
                rows={9}
                maxLength={30_000}
                lang={language === 'tw' ? 'zh-Hant' : language}
                dir={language === 'ar' ? 'rtl' : 'ltr'}
                aria-describedby="guidance-save-scope"
                aria-invalid={!valid}
                className="field-sizing-fixed min-h-56 resize-y leading-relaxed"
              />
              {!valid && (
                <p className="text-sm text-destructive" role="alert">
                  {t('admin.guidance.validation')}
                </p>
              )}
            </div>
            <div className="space-y-4 border-t border-border pt-6">
              <div className="flex flex-wrap items-center gap-3">
                <Label htmlFor="guidance-preview-role">
                  {t('admin.guidance.previewRole')}
                </Label>
                <select
                  id="guidance-preview-role"
                  value={selectedPreviewRole ?? ''}
                  className={selectClass}
                  disabled={!previewRoles.length}
                  onChange={event =>
                    setPreviewRole(event.target.value as ActualRole)}
                >
                  {previewRoles.map(role => (
                    <option key={role} value={role}>
                      [{roleName(role)}]
                    </option>
                  ))}
                  {!previewRoles.length && (
                    <option value="">{t('admin.guidance.noRoles')}</option>
                  )}
                </select>
                <Button
                  variant="outline"
                  disabled={!previewInput || !valid}
                  onClick={() => setPreview(previewInput)}
                  data-testid="guidance-preview-button"
                >
                  {t('admin.guidance.preview')}
                </Button>
              </div>
              {selectedPreviewRole && (
                <p className="text-sm text-muted-foreground">
                  {t(`admin.guidance.${RoleFaction[selectedPreviewRole]}`)} + [
                  {roleName(selectedPreviewRole)}]
                  {hasSheriff
                    ? ` + ${t(`admin.guidance.${RoleFaction[selectedPreviewRole]}`)} ${t('admin.guidance.sheriff')}`
                    : ''}
                </p>
              )}
              {preview ? (
                <GuidancePreview
                  input={preview}
                  stale={previewStale}
                  dirty={dirty}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('admin.guidance.previewHint')}
                </p>
              )}
            </div>
          </>
        )}
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
          <div className="min-w-0 space-y-2 text-sm" aria-live="polite">
            <p data-testid="guidance-save-status">
              {t(
                dirty
                  ? 'admin.guidance.dirty'
                  : savedLanguage === language
                    ? 'admin.guidance.saved'
                    : 'admin.guidance.noChanges',
              )}
            </p>
            {anyDirty && !dirty && (
              <p className="text-muted-foreground">
                {t('admin.guidance.otherDirty')}
              </p>
            )}
            <p id="guidance-save-scope" className="text-muted-foreground">
              {t('admin.guidance.saveScope')}
            </p>
            {saveMutation.error && (
              <p className="text-destructive" role="alert">
                {t('admin.guidance.saveFailed')} {saveMutation.error.message}
              </p>
            )}
          </div>
          <Button
            disabled={!dirty || !valid || saveMutation.isPending || isFetching}
            onClick={save}
            data-testid="guidance-save"
          >
            {saveMutation.isPending && (
              <Loader2 className="mr-2 size-4 animate-spin" />
            )}
            {t(
              saveMutation.isPending
                ? 'admin.guidance.saving'
                : 'admin.guidance.save',
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
